import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { uiLocale } from "@/i18n";
import { enumLabel } from "@/i18n/enumLabels";
import { api } from "@/api/client";
import {
  COMPLAINT_TYPES,
  COMPLAINT_TYPE_LABELS,
  COMPLAINT_STATUS_LABELS,
  type ComplaintType,
  type ComplaintStatus,
} from "@ckgo2/shared/types";
import { MessageSquareWarning, Loader2, Plus } from "lucide-react";

interface Complaint {
  id: string;
  type: string;
  status: ComplaintStatus;
  title: string;
  compensationAmount: number | null;
  currency: string;
  resolvedAt: string | null;
}

const STATUS_COLOR: Record<ComplaintStatus, string> = {
  open: "bg-amber-100 text-amber-700",
  in_progress: "bg-blue-100 text-blue-700",
  resolved: "bg-emerald-100 text-emerald-700",
  rejected: "bg-gray-100 text-gray-600",
};

/** Complaints for one order (Block 2 B2.7): list, create, and resolve with a compensation refund. */
export default function ComplaintsPanel({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", type: "service_quality", compensationAmount: "" });
  const [resolving, setResolving] = useState<string | null>(null);
  const [resolveForm, setResolveForm] = useState({
    compensationAmount: "",
    issueCreditNote: false,
    note: "",
  });

  const list = useQuery({
    queryKey: ["complaints", orderId],
    queryFn: () =>
      api.get<{ data: Complaint[] }>(`/api/admin/complaints?orderId=${orderId}&limit=50`),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["complaints", orderId] });
    qc.invalidateQueries({ queryKey: ["order-audit", orderId] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.post(`/api/admin/complaints`, {
        orderId,
        title: form.title,
        type: form.type,
        compensationAmount: form.compensationAmount ? Number(form.compensationAmount) : null,
      }),
    onSuccess: () => {
      invalidate();
      setAdding(false);
      setForm({ title: "", type: "service_quality", compensationAmount: "" });
    },
  });

  const resolve = useMutation({
    mutationFn: (id: string) =>
      api.post(`/api/admin/complaints/${id}/resolve`, {
        status: "resolved",
        resolutionNote: resolveForm.note || null,
        compensationAmount: resolveForm.compensationAmount
          ? Number(resolveForm.compensationAmount)
          : null,
        issueCreditNote: resolveForm.issueCreditNote,
      }),
    onSuccess: () => {
      invalidate();
      setResolving(null);
      setResolveForm({ compensationAmount: "", issueCreditNote: false, note: "" });
    },
  });

  const items = list.data?.data ?? [];

  return (
    <div className="card">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
          <MessageSquareWarning className="h-5 w-5" /> {t("orders.complaints.title")}
        </h2>
        <button onClick={() => setAdding((v) => !v)} className="btn-ghost btn-sm">
          <Plus className="h-4 w-4" /> {t("orders.complaints.new")}
        </button>
      </div>

      {adding && (
        <div className="mb-3 space-y-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-800">
          <input
            className="input"
            placeholder={t("orders.complaints.whatPlaceholder")}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
          <div className="grid grid-cols-2 gap-2">
            <select
              className="input"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
            >
              {COMPLAINT_TYPES.map((type) => (
                <option key={type} value={type}>
                  {enumLabel(COMPLAINT_TYPE_LABELS, type)}
                </option>
              ))}
            </select>
            <input
              className="input"
              type="number"
              placeholder={t("orders.complaints.compensationPlaceholder")}
              value={form.compensationAmount}
              onChange={(e) => setForm({ ...form, compensationAmount: e.target.value })}
            />
          </div>
          <button
            onClick={() => create.mutate()}
            disabled={!form.title.trim() || create.isPending}
            className="btn-primary btn-sm w-full"
          >
            {create.isPending ? (
              <Loader2 className="mx-auto h-4 w-4 animate-spin" />
            ) : (
              t("orders.complaints.add")
            )}
          </button>
        </div>
      )}

      {list.isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : items.length === 0 ? (
        <p className="py-2 text-sm text-gray-500">{t("orders.complaints.empty")}</p>
      ) : (
        <div className="space-y-2">
          {items.map((cmp) => (
            <div
              key={cmp.id}
              className="rounded-lg border border-gray-200 p-3 dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {cmp.title}
                </span>
                <span
                  className={`rounded px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLOR[cmp.status]}`}
                >
                  {enumLabel(COMPLAINT_STATUS_LABELS, cmp.status)}
                </span>
              </div>
              <div className="mt-0.5 text-xs text-gray-500">
                {enumLabel(COMPLAINT_TYPE_LABELS, cmp.type as ComplaintType)}
                {cmp.compensationAmount
                  ? ` · ${new Intl.NumberFormat(uiLocale()).format(cmp.compensationAmount)} ${cmp.currency}`
                  : ""}
              </div>

              {cmp.status !== "resolved" && cmp.status !== "rejected" && (
                <div className="mt-2">
                  {resolving === cmp.id ? (
                    <div className="space-y-2">
                      <input
                        className="input"
                        type="number"
                        placeholder={t("orders.complaints.compensationToPay")}
                        value={resolveForm.compensationAmount}
                        onChange={(e) =>
                          setResolveForm({ ...resolveForm, compensationAmount: e.target.value })
                        }
                      />
                      <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input
                          type="checkbox"
                          checked={resolveForm.issueCreditNote}
                          onChange={(e) =>
                            setResolveForm({ ...resolveForm, issueCreditNote: e.target.checked })
                          }
                        />
                        {t("orders.complaints.issueCreditNote")}
                      </label>
                      <input
                        className="input"
                        placeholder={t("orders.complaints.resolutionNote")}
                        value={resolveForm.note}
                        onChange={(e) => setResolveForm({ ...resolveForm, note: e.target.value })}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => resolve.mutate(cmp.id)}
                          disabled={resolve.isPending}
                          className="btn-primary btn-sm flex-1"
                        >
                          {resolve.isPending ? (
                            <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                          ) : (
                            t("orders.complaints.resolveRefund")
                          )}
                        </button>
                        <button onClick={() => setResolving(null)} className="btn-ghost btn-sm">
                          {t("common.actions.cancel")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setResolving(cmp.id);
                        setResolveForm({
                          compensationAmount: cmp.compensationAmount
                            ? String(cmp.compensationAmount)
                            : "",
                          issueCreditNote: false,
                          note: "",
                        });
                      }}
                      className="btn-ghost btn-sm"
                    >
                      {t("orders.complaints.resolve")}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
